import { useState } from "react";
import { Button } from "@/components/ui/button";
import { FileDown, Loader2, CheckCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { generateProjectReport } from "@/lib/generateProjectReport";

interface ProjectReportButtonProps {
  variant?: "default" | "outline" | "secondary";
  size?: "default" | "sm" | "lg";
  className?: string;
}

export const ProjectReportButton = ({
  variant = "default",
  size = "default",
  className = "",
}: ProjectReportButtonProps) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const { toast } = useToast();

  const handleGenerateReport = async () => {
    setIsGenerating(true);
    setIsComplete(false);
    
    try {
      await generateProjectReport();
      
      setIsComplete(true);
      toast({
        title: "Report Generated Successfully!",
        description: "OFUESI-Project-Report.pptx has been downloaded.",
      });
      
      // Reset complete state after 3 seconds
      setTimeout(() => setIsComplete(false), 3000);
    } catch (error) {
      console.error("Error generating report:", error);
      toast({
        title: "Error Generating Report",
        description: "Please try again. If the problem persists, contact support.",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Button
      variant={variant}
      size={size}
      className={className}
      onClick={handleGenerateReport}
      disabled={isGenerating}
    >
      {isGenerating ? (
        <>
          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          Generating Report...
        </>
      ) : isComplete ? (
        <>
          <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
          Downloaded!
        </>
      ) : (
        <>
          <FileDown className="w-4 h-4 mr-2" />
          Generate Project Report
        </>
      )}
    </Button>
  );
};

export default ProjectReportButton;
